import React, { useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink } from 'react-router-dom';
import FlashBetSportDto from '../models/dtos/FlashBetSportDto';
import FlashBetSportPromptSummaryDto from '../models/dtos/FlashBetSportPromptSummaryDto';
import PromptMacrosDto from '../models/dtos/PromptMacrosDto';
import {
    createSportPrompt,
    deleteSportPrompt,
    getFlashBetPromptsHubBundle,
    getPromptMacros,
    getSportPrompts,
    setActivePrompt,
    updateSportPrompt
} from '../Api';

interface SportAiSettingsModalProps {
    open: boolean;
    sport: FlashBetSportDto | null;
    onClose: () => void;
    initialPromptId?: string;
    saveAndClose?: boolean; // If true, Save button will close the modal immediately
}

type VariantForm = { name: string; questionTaskCode: string; eventsTaskCode: string };

const EMPTY_FORM: VariantForm = { name: '', questionTaskCode: '', eventsTaskCode: '' };
const TASK_CODE = /^[a-z0-9][a-z0-9._-]{0,127}$/;

/** A hub task code with a link to its editor (AI › Tasks). */
function TaskLink({ code }: { code: string }) {
    return (
        <Link component={RouterLink} to={`/ai/tasks/${encodeURIComponent(code)}`} target="_blank" rel="noopener"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontFamily: 'monospace', fontSize: '0.85rem' }}>
            {code}<OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
    );
}

/**
 * Flash Bet prompt variants of a sport. A variant only names the betup-server-ai tasks it uses (events + questions);
 * the prompts, models and costs live in the AI hub (AI › Tasks). One variant per sport is active.
 */
const SportAiSettingsModal: React.FC<SportAiSettingsModalProps> = ({ open, sport, onClose, initialPromptId, saveAndClose = false }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [prompts, setPrompts] = useState<FlashBetSportPromptSummaryDto[]>([]);
    const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
    const [isCreatingNew, setIsCreatingNew] = useState<boolean>(false);
    const [form, setForm] = useState<VariantForm>(EMPTY_FORM);
    const [macros, setMacros] = useState<PromptMacrosDto | null>(null);
    const [bundleModel, setBundleModel] = useState<string>('claude-sonnet-5-5');
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const selected = prompts.find(p => p.id === selectedPromptId) ?? null;
    const legacyCount = prompts.filter(p => p.hasLegacyPrompts).length;

    useEffect(() => {
        if (open && sport) {
            loadPrompts(initialPromptId);
            getPromptMacros().then(setMacros).catch(() => setMacros(null));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sport]);

    useEffect(() => {
        if (isCreatingNew) return;
        setForm(selected ? { name: selected.name, questionTaskCode: selected.questionTaskCode, eventsTaskCode: selected.eventsTaskCode } : EMPTY_FORM);
    }, [selected, isCreatingNew]);

    const loadPrompts = async (preferId?: string | null) => {
        if (!sport) return;
        setLoading(true);
        try {
            const list = await getSportPrompts(sport.id);
            setPrompts(list);
            const keep = list.find(p => p.id === preferId) ?? list.find(p => p.isActive) ?? list[0];
            setSelectedPromptId(keep?.id ?? null);
        } catch (e) {
            setErrorMessage(e instanceof Error ? e.message : 'Failed to load prompt variants');
        } finally {
            setLoading(false);
        }
    };

    const formError = (): string | null => {
        if (!form.name.trim()) return 'Name is required';
        for (const code of [form.questionTaskCode, form.eventsTaskCode]) {
            if (code.trim() && !TASK_CODE.test(code.trim())) return `'${code}' is not a task code (lowercase letters, digits, '.', '_', '-')`;
        }
        return null;
    };

    const hasChanges = isCreatingNew || (selected != null && (
        form.name !== selected.name || form.questionTaskCode !== selected.questionTaskCode || form.eventsTaskCode !== selected.eventsTaskCode));

    const handleSave = async () => {
        if (!sport) return;
        const error = formError();
        if (error) { setErrorMessage(error); return; }
        const request = {
            name: form.name.trim(),
            questionTaskCode: form.questionTaskCode.trim() || null,
            eventsTaskCode: form.eventsTaskCode.trim() || null,
        };
        setLoading(true);
        try {
            const saved = isCreatingNew
                ? await createSportPrompt(sport.id, request)
                : await updateSportPrompt(sport.id, selectedPromptId!, request);
            setIsCreatingNew(false);
            setSuccessMessage(isCreatingNew ? 'Variant created' : 'Variant saved');
            if (saveAndClose && !isCreatingNew) { onClose(); return; }
            await loadPrompts(saved?.id ?? selectedPromptId);
        } catch (e) {
            setErrorMessage(e instanceof Error ? e.message : 'Failed to save the variant');
        } finally {
            setLoading(false);
        }
    };

    const handleSetActive = async () => {
        if (!sport || !selectedPromptId) return;
        setLoading(true);
        try {
            await setActivePrompt(sport.id, { promptId: selectedPromptId });
            setSuccessMessage('Active variant changed');
            await loadPrompts(selectedPromptId);
        } catch (e) {
            setErrorMessage(e instanceof Error ? e.message : 'Failed to change the active variant');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!sport || !selected) return;
        if (!window.confirm(`Delete variant "${selected.name}"? Its hub tasks stay in AI › Tasks.`)) return;
        setLoading(true);
        const ok = await deleteSportPrompt(sport.id, selected.id);
        setLoading(false);
        if (ok) {
            setSuccessMessage('Variant deleted');
            await loadPrompts(null);
        } else {
            setErrorMessage('Failed to delete the variant (the default and the active variant cannot be deleted)');
        }
    };

    const handleDownloadBundle = async () => {
        try {
            const bundle = await getFlashBetPromptsHubBundle(bundleModel.trim());
            const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'flashbet-prompts-hub-bundle.json';
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErrorMessage(e instanceof Error ? e.message : 'Failed to export the prompts');
        }
    };

    const handleClose = () => {
        setIsCreatingNew(false);
        setPrompts([]);
        setSelectedPromptId(null);
        onClose();
    };

    const macroList = (title: string, values?: { [key: string]: string }) => (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2">{title}</Typography>
            {Object.entries(values ?? {}).map(([name, description]) => (
                <Typography key={name} variant="body2" sx={{ ml: 1 }}>
                    <code>{`{{${name}}}`}</code> — {description}
                </Typography>
            ))}
        </Box>
    );

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                        {sport?.photoUrl && <Avatar src={sport.photoUrl} sx={{ width: 32, height: 32 }} />}
                        <Typography variant="h6">{sport?.name ?? 'Sport'} · Flash Bet AI</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack gap={2} sx={{ mt: 1 }}>
                        <Alert severity="info">
                            Prompts, models and costs live in the AI hub (AI › Tasks). A variant chooses which hub tasks
                            process the match events and generate the questions; the active variant is used by default.
                        </Alert>

                        {legacyCount > 0 && (
                            <Alert severity="warning">
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {legacyCount} variant(s) of this sport still carry prompt texts from before the AI hub.
                                    Download them as a hub bundle once and import it in AI › Import (mode UPSERT) — until then
                                    their hub tasks may be missing or still hold the seeded default prompt.
                                </Typography>
                                <Stack direction="row" gap={1} alignItems="center">
                                    <TextField size="small" label="Model for the tasks" value={bundleModel}
                                               onChange={e => setBundleModel(e.target.value)} sx={{ width: 240 }} />
                                    <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadBundle}
                                            disabled={!bundleModel.trim()}>
                                        Download hub bundle
                                    </Button>
                                </Stack>
                            </Alert>
                        )}

                        <Stack direction="row" gap={1} alignItems="center">
                            <FormControl fullWidth disabled={isCreatingNew || loading}>
                                <InputLabel id="variant-select-label">Variant</InputLabel>
                                <Select labelId="variant-select-label" label="Variant" value={selectedPromptId ?? ''}
                                        onChange={e => setSelectedPromptId(e.target.value as string)}>
                                    {prompts.map(p => (
                                        <MenuItem key={p.id} value={p.id}>
                                            <Stack direction="row" gap={1} alignItems="center">
                                                <span>{p.name}</span>
                                                {p.isDefault && <Chip label="Default" size="small" />}
                                                {p.isActive && <Chip label="Active" size="small" color="success" icon={<CheckCircleIcon />} />}
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button startIcon={<AddIcon />} variant="outlined" disabled={loading || isCreatingNew}
                                    onClick={() => { setIsCreatingNew(true); setForm(EMPTY_FORM); }}>
                                New
                            </Button>
                        </Stack>

                        {loading && prompts.length === 0 ? (
                            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
                        ) : (isCreatingNew || selected) && (
                            <Stack gap={2}>
                                <TextField label="Name" value={form.name} disabled={selected?.isDefault && !isCreatingNew}
                                           onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
                                <TextField label="Questions task (hub)" value={form.questionTaskCode}
                                           disabled={selected?.isDefault && !isCreatingNew}
                                           onChange={e => setForm(f => ({ ...f, questionTaskCode: e.target.value }))}
                                           helperText="Blank: derived from the sport and the name" fullWidth />
                                <TextField label="Events task (hub)" value={form.eventsTaskCode}
                                           disabled={selected?.isDefault && !isCreatingNew}
                                           onChange={e => setForm(f => ({ ...f, eventsTaskCode: e.target.value }))}
                                           helperText="Blank: derived from the sport and the name" fullWidth />
                                {!isCreatingNew && selected && (
                                    <Stack gap={0.5}>
                                        <Typography variant="body2">Questions: <TaskLink code={selected.questionTaskCode} /></Typography>
                                        <Typography variant="body2">Events: <TaskLink code={selected.eventsTaskCode} /></Typography>
                                        {selected.isDefault && (
                                            <Typography variant="caption" color="text.secondary">
                                                The default variant always uses the seeded tasks; edit their prompts in AI › Tasks.
                                            </Typography>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        )}

                        <Accordion disableGutters variant="outlined">
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2">Variables games sends to the tasks (use them as {'{{NAME}}'} in the hub prompts)</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {macroList('Events task', macros?.eventPromptMacros)}
                                {macroList('Questions task', macros?.questionPromptMacros)}
                            </AccordionDetails>
                        </Accordion>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {!isCreatingNew && selected && !selected.isDefault && !selected.isActive && (
                        <Button color="error" startIcon={<DeleteIcon />} onClick={handleDelete} disabled={loading} sx={{ mr: 'auto' }}>
                            Delete
                        </Button>
                    )}
                    {!isCreatingNew && selected && !selected.isActive && (
                        <Button onClick={handleSetActive} disabled={loading}>Set active</Button>
                    )}
                    {isCreatingNew && <Button onClick={() => setIsCreatingNew(false)} disabled={loading}>Cancel</Button>}
                    <Button onClick={handleClose} disabled={loading}>Close</Button>
                    <Button onClick={handleSave} variant="contained"
                            disabled={loading || !hasChanges || (selected?.isDefault === true && !isCreatingNew)}>
                        {isCreatingNew ? 'Create' : (saveAndClose ? 'Save & Close' : 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={successMessage !== null} autoHideDuration={4000} onClose={() => setSuccessMessage(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>{successMessage}</Alert>
            </Snackbar>
            <Snackbar open={errorMessage !== null} autoHideDuration={6000} onClose={() => setErrorMessage(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>{errorMessage}</Alert>
            </Snackbar>
        </>
    );
};

export default SportAiSettingsModal;
